/**
 * @module core/auth/auth-manager
 * @description Main authentication module handling Firebase auth initialization,
 * sign in/out operations, auth state changes, and user context management
 */

import { initializeApp } from 'https://www.gstatic.com/firebasejs/12.19.0/firebase-app.js';
import {
    getAuth,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged
} from 'https://www.gstatic.com/firebasejs/12.19.0/firebase-auth.js';

import { isValidUserDomain, getEmailDomain, REQUIRED_DOMAIN } from './auth-validators.js';
import {
    initializeTokenManager,
    startTokenRefreshInterval,
    stopTokenRefreshInterval
} from './token-manager.js';
import { logger } from '../../utils/logger.js';
import { safeMonitoring as monitoring, ErrorCategory, ErrorSeverity } from '../../services/monitoring-safe.js';
import { showError } from '../../ui/components/error-display.js';

/**
 * Firebase app instance
 * @type {Object|null}
 */
let app = null;

/**
 * Firebase auth instance
 * @type {Object|null}
 */
let auth = null;

/**
 * Google auth provider instance
 * @type {Object|null}
 */
let provider = null;

/**
 * Current authenticated user
 * @type {Object|null}
 */
let currentUser = null;

/**
 * Auth state change callback functions
 * @type {Function[]}
 */
const authStateCallbacks = [];

/**
 * Initialize Firebase authentication
 * Must be called before any other auth operations
 *
 * @param {Object} firebaseConfig - Firebase configuration object
 * @throws {Error} If Firebase config is invalid or initialization fails
 *
 * @example
 * import { firebaseConfig } from './config/firebase-config.js';
 * await initializeAuth(firebaseConfig);
 */
export function initializeAuth(firebaseConfig) {
    if (!firebaseConfig) {
        throw new Error('Firebase configuration is required');
    }

    try {
        // Initialize Firebase
        app = initializeApp(firebaseConfig);
        auth = getAuth(app);
        provider = new GoogleAuthProvider();

        // Configure Google provider for workspace domain
        provider.setCustomParameters({
            hd: REQUIRED_DOMAIN
        });

        // Initialize token manager with auth instance
        initializeTokenManager(auth);

        // Set up auth state listener
        setupAuthStateListener();

        logger.info('✅ Firebase authentication initialized');

        return auth;
    } catch (error) {
        logger.error('❌ Failed to initialize Firebase authentication:', error);

        // Track initialization error
        if (monitoring.isInitialized()) {
            monitoring.trackError(error, {
                category: ErrorCategory.AUTHENTICATION,
                severity: ErrorSeverity.CRITICAL,
                operation: 'initializeAuth'
            });
        }

        throw error;
    }
}

/**
 * Get the current Firebase auth instance
 *
 * @returns {Object|null} Firebase auth instance
 * @throws {Error} If auth is not initialized
 */
export function getAuthInstance() {
    if (!auth) {
        throw new Error('Auth not initialized. Call initializeAuth first.');
    }
    return auth;
}

/**
 * Sign in with Google popup
 *
 * @returns {Promise<Object>} User credential result
 * @throws {Error} If sign in fails
 *
 * @example
 * try {
 *   const result = await signInWithGoogle();
 *   console.log('Signed in:', result.user.email);
 * } catch (error) {
 *   console.error('Sign in failed:', error);
 * }
 */
export async function signInWithGoogle() {
    if (!auth || !provider) {
        throw new Error('Auth not initialized. Call initializeAuth first.');
    }

    try {
        const result = await signInWithPopup(auth, provider);
        logger.info('✅ User signed in:', result.user.email);
        return result;
    } catch (error) {
        logger.error('❌ Error signing in:', error);

        // Track sign in error
        if (monitoring.isInitialized()) {
            monitoring.trackError(error, {
                category: ErrorCategory.AUTHENTICATION,
                severity: ErrorSeverity.ERROR,
                operation: 'signInWithGoogle'
            });
        }

        throw error;
    }
}

/**
 * Sign out the current user
 *
 * @returns {Promise<void>}
 * @throws {Error} If sign out fails
 *
 * @example
 * await signOutUser();
 */
export async function signOutUser() {
    if (!auth) {
        throw new Error('Auth not initialized. Call initializeAuth first.');
    }

    try {
        // Stop token refresh interval
        stopTokenRefreshInterval();

        // Sign out from Firebase
        await signOut(auth);

        // Clear current user reference
        currentUser = null;

        logger.info('✅ User signed out');
    } catch (error) {
        logger.error('❌ Error signing out:', error);

        // Track sign out error
        if (monitoring.isInitialized()) {
            monitoring.trackError(error, {
                category: ErrorCategory.AUTHENTICATION,
                severity: ErrorSeverity.ERROR,
                operation: 'signOutUser'
            });
        }

        throw error;
    }
}

/**
 * Get the current authenticated user
 *
 * @returns {Object|null} Current user object or null if not authenticated
 *
 * @example
 * const user = getCurrentUser();
 * if (user) {
 *   console.log('Current user:', user.email);
 * }
 */
export function getCurrentUser() {
    return currentUser;
}

/**
 * Check if user is authenticated
 *
 * @returns {boolean} True if user is authenticated
 *
 * @example
 * if (isAuthenticated()) {
 *   // Show authenticated content
 * }
 */
export function isAuthenticated() {
    return currentUser !== null;
}

/**
 * Register a callback for auth state changes
 * Callback will be invoked with user object (or null) when auth state changes
 *
 * @param {Function} callback - Function to call on auth state change
 * @returns {Function} Unsubscribe function
 *
 * @example
 * const unsubscribe = onAuthStateChange((user) => {
 *   if (user) {
 *     console.log('User signed in:', user.email);
 *   } else {
 *     console.log('User signed out');
 *   }
 * });
 *
 * // Later, to unsubscribe:
 * unsubscribe();
 */
export function onAuthStateChange(callback) {
    if (typeof callback !== 'function') {
        throw new Error('Callback must be a function');
    }

    authStateCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
        const index = authStateCallbacks.indexOf(callback);
        if (index > -1) {
            authStateCallbacks.splice(index, 1);
        }
    };
}

/**
 * Set up Firebase auth state listener
 * Internal function called during initialization
 * @private
 */
function setupAuthStateListener() {
    onAuthStateChanged(auth, async (user) => {
        // Clear any existing token refresh interval
        stopTokenRefreshInterval();

        if (user) {
            // Validate user domain at runtime
            const email = user.email;
            const domain = getEmailDomain(email);

            if (!isValidUserDomain(email)) {
                logger.error(`❌ Access denied for domain: ${domain}`);
                showError(`Access restricted to ${REQUIRED_DOMAIN} users only.`);
                await signOutUser();
                return;
            }

            // User is signed in and authorized
            currentUser = user;

            // Set up proactive token refresh
            startTokenRefreshInterval(user);

            logger.info('✅ User authenticated:', email);
        } else {
            // User is signed out
            currentUser = null;
            logger.info('ℹ️ User signed out');
        }

        // Notify all registered callbacks
        notifyAuthStateCallbacks(user);
    });
}

/**
 * Notify all registered auth state change callbacks
 * @private
 * @param {Object|null} user - Current user or null
 */
function notifyAuthStateCallbacks(user) {
    authStateCallbacks.forEach(callback => {
        try {
            callback(user);
        } catch (error) {
            logger.error('Error in auth state callback:', error);

            // Track callback error
            if (monitoring.isInitialized()) {
                monitoring.trackError(error, {
                    category: ErrorCategory.AUTHENTICATION,
                    severity: ErrorSeverity.WARNING,
                    operation: 'authStateCallback'
                });
            }
        }
    });
}

/**
 * Force refresh the current user's auth token
 *
 * @returns {Promise<string>} New auth token
 * @throws {Error} If no user is authenticated
 *
 * @example
 * const token = await refreshAuthToken();
 */
export async function refreshAuthToken() {
    if (!currentUser) {
        throw new Error('No authenticated user');
    }

    try {
        const token = await currentUser.getIdToken(true);
        logger.info('✅ Auth token refreshed');
        return token;
    } catch (error) {
        logger.error('❌ Failed to refresh auth token:', error);

        // Track token refresh error
        if (monitoring.isInitialized()) {
            monitoring.trackError(error, {
                category: ErrorCategory.AUTHENTICATION,
                severity: ErrorSeverity.ERROR,
                operation: 'refreshAuthToken'
            });
        }

        throw error;
    }
}

/**
 * Get the current user's email
 *
 * @returns {string|null} User email or null if not authenticated
 *
 * @example
 * const email = getUserEmail();
 */
export function getUserEmail() {
    return currentUser?.email || null;
}

/**
 * Get the current user's display name
 *
 * @returns {string|null} User display name or null if not authenticated
 *
 * @example
 * const name = getUserDisplayName();
 */
export function getUserDisplayName() {
    return currentUser?.displayName || null;
}

/**
 * Get the current user's photo URL
 *
 * @returns {string|null} User photo URL or null if not authenticated
 *
 * @example
 * const photoURL = getUserPhotoURL();
 */
export function getUserPhotoURL() {
    return currentUser?.photoURL || null;
}
